import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import {
  Vehicle,
  CreateVehicleDto,
  UpdateVehicleDto,
  VehicleStatus
} from '../models/vehicle.model';

/**
 * Service for managing vehicles (motorcycles)
 * Handles CRUD operations for vehicle entities
 */
@Injectable({
  providedIn: 'root'
})
export class VehicleService {
  private readonly API_URL = `${environment.apiUrl}/global_admin/vehicles`;

  constructor(private http: HttpClient) {}

  /**
   * Get all vehicles
   */
  getAllVehicles(params?: {
    teamId?: string;
    status?: VehicleStatus;
    page?: number;
    limit?: number;
  }): Observable<Vehicle[]> {
    let httpParams = new HttpParams();

    if (params) {
      if (params.teamId) {
        httpParams = httpParams.set('teamId', params.teamId);
      }
      if (params.status) {
        httpParams = httpParams.set('status', params.status);
      }
      if (params.page !== undefined) {
        httpParams = httpParams.set('page', params.page.toString());
      }
      if (params.limit !== undefined) {
        httpParams = httpParams.set('limit', params.limit.toString());
      }
    }

    return this.http.get<Vehicle[]>(this.API_URL, { params: httpParams });
  }

  /**
   * Get vehicles for a specific team
   */
  getVehiclesByTeam(teamId: string): Observable<Vehicle[]> {
    return this.http.get<Vehicle[]>(`${this.API_URL}/team/${teamId}`);
  }

  /**
   * Get a single vehicle by ID
   */
  getVehicle(id: string): Observable<Vehicle> {
    return this.http.get<Vehicle>(`${this.API_URL}/${id}`);
  }

  /**
   * Create a new vehicle
   */
  createVehicle(vehicle: CreateVehicleDto): Observable<Vehicle> {
    return this.http.post<Vehicle>(this.API_URL, vehicle);
  }

  /**
   * Update an existing vehicle
   */
  updateVehicle(id: string, vehicle: UpdateVehicleDto): Observable<Vehicle> {
    return this.http.put<Vehicle>(`${this.API_URL}/${id}`, vehicle);
  }

  /**
   * Delete a vehicle
   */
  deleteVehicle(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_URL}/${id}`);
  }

  /**
   * Update vehicle status
   */
  updateVehicleStatus(id: string, status: VehicleStatus): Observable<Vehicle> {
    return this.http.patch<Vehicle>(`${this.API_URL}/${id}/status`, { status });
  }

  /**
   * Get active vehicles for a team
   */
  getActiveVehiclesByTeam(teamId: string): Observable<Vehicle[]> {
    return this.http.get<Vehicle[]>(`${this.API_URL}/team/${teamId}/active`);
  }

  /**
   * Upload vehicle image
   */
  uploadVehicleImage(id: string, image: File): Observable<Vehicle> {
    const formData = new FormData();
    formData.append('image', image);
    return this.http.post<Vehicle>(`${this.API_URL}/${id}/image`, formData);
  }

  /**
   * Get vehicle statistics
   */
  getVehicleStats(id: string): Observable<any> {
    return this.http.get(`${this.API_URL}/${id}/stats`);
  }
}
